import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef } from 'react';

import { styled, useForkRef } from '@mui/material';
import { useClickOutside, useTranslation } from '@kc-console/core';

import { MenuContainer } from '../MenuContainer';
import DropdownMenu2Popper from './DropdownMenu2Popper';
import { dropdownMenu2Classes } from './theme';
import { useComputedAnchor } from './utils';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { MenuContainerApiRef } from '../MenuContainer';
import type { MenuListItemData, MenuListOptionItemData } from '../MenuList';
import type {
  DropdownMenu2ApiRef,
  DropdownMenu2Props,
  DropdownMenuColor,
  DropdownMenuItemData,
  DropdownMenuOptionItemData,
  DropdownMenuSectionItemData,
} from './types';

// ============================================================================
// Main Component
// ============================================================================

export default function DropdownMenu2(props: DropdownMenu2Props) {
  const { t } = useTranslation();
  const {
    anchorEl,
    anchorPosition,
    anchorReference: anchorReferenceProp,
    apiRef,
    autoFocusItem = true,
    className,
    closeOnOptionClick = true,
    color = 'default',
    disablePortal = false,
    disableTooltipOnEllipsis = false,
    ellipsisTooltipPlacement = 'right',
    enableHighlight = true,
    expandable,
    filterItems,
    footer,
    header,
    items,
    marginThreshold = 16,
    noFilteredOptionsText = t('common:common.list.no_matching_items'),
    noOptionsText = t('common:common.list.no_selectable_items'),
    onClose,
    onSearchTextChange,
    open,
    origin,
    placement = 'bottom-start',
    ref,
    searchable = false,
    searchPlaceholder = t('common:common.general.enter_search_term'),
    searchText: searchTextProp,
    selectedType = 'check',
    slotProps,
    zIndex,
  } = props;

  // ========== Anchor 계산 ==========
  const anchorReference = anchorReferenceProp ?? (anchorPosition ? 'anchorPosition' : 'anchorEl');
  const derivedAnchorEl = useComputedAnchor(anchorEl, anchorPosition, anchorReference);

  // ========== Items를 MenuListItemData로 변환 ==========
  const transformOptionItem = useCallback(
    (item: DropdownMenuOptionItemData, _itemKey: string): MenuListOptionItemData => {
      const { renderItem, onClick, ...rest } = item;

      return {
        ...rest,
        onClick: (event: MouseEvent<HTMLLIElement>) => {
          if (item.disabled || item.interactive === false) return;
          onClick?.(event);
          if (closeOnOptionClick && !event.defaultPrevented) {
            onClose?.(event, 'optionClick');
          }
        },
        renderItem: renderItem
          ? props => {
              const { selectedType: propsSelectedType, ...restProps } = props;
              return renderItem({
                ...restProps,
                selectedType: propsSelectedType === 'none' ? undefined : propsSelectedType,
                closeOnOptionClick,
                onClose,
              });
            }
          : undefined,
      };
    },
    [closeOnOptionClick, onClose],
  );

  const menuListItems = useMemo((): readonly MenuListItemData[] => {
    return items.map((item: DropdownMenuItemData, index): MenuListItemData => {
      if (item.type === 'section') {
        const sectionItem = item as DropdownMenuSectionItemData;
        return {
          ...sectionItem,
          items: sectionItem.items?.map((child, childIndex) =>
            transformOptionItem(child, `${index}-${childIndex}`),
          ),
        };
      }
      return transformOptionItem(item as DropdownMenuOptionItemData, `${index}`);
    });
  }, [items, transformOptionItem]);

  // ========== MenuContainer API ref ==========
  const menuContainerApiRef = useRef<MenuContainerApiRef>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const autoFocusSearchInput = slotProps?.menuContainer?.autoFocusSearchInput ?? searchable;
  const shouldAutoFocusOnEntered = searchable ? autoFocusSearchInput : autoFocusItem;

  // ========== 외부 제어 API ==========
  useImperativeHandle<DropdownMenu2ApiRef, DropdownMenu2ApiRef>(
    apiRef,
    () => ({
      get focusedItemKey() {
        return menuContainerApiRef.current?.focusedItemKey ?? null;
      },
      navigateUp: () => {
        return menuContainerApiRef.current?.navigateUp() ?? null;
      },
      navigateDown: () => {
        return menuContainerApiRef.current?.navigateDown() ?? null;
      },
      selectFocused: () => {
        return menuContainerApiRef.current?.selectFocused() ?? false;
      },
      setFocusedKey: key => {
        menuContainerApiRef.current?.setFocusedKey(key);
      },
      focusTrigger: () => {
        menuContainerApiRef.current?.focusTrigger();
      },
    }),
    [],
  );

  // Transition 완료 후 포커스 설정
  const handleTransitionEntered = () => {
    if (shouldAutoFocusOnEntered) {
      menuContainerApiRef.current?.focusTrigger();
    }
  };

  // ========== 기본 키보드 처리 (Tab/Escape) ==========
  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.(event, 'escapeKeyDown');
      return;
    }

    if (event.key === 'Tab' && slotProps?.menuList?.role === 'listbox') {
      onClose?.(event, 'tabKeyDown');
    }
  };

  // ========== anchorEl HTMLElement 참조 ==========
  const anchorElHtmlElement =
    typeof document !== 'undefined' && !!anchorEl && anchorEl instanceof HTMLElement
      ? anchorEl
      : null;

  // ========== Click Outside ==========
  const anchorElToggleFallbackId = useId();
  const clickOutsideTargetRef = useClickOutside({
    enabled: open,
    onClickOutside: () => onClose?.({} as Event, 'backdropClick'),
    ignoreSelectors: anchorElHtmlElement
      ? [`[data-toggle-id="${anchorElHtmlElement.dataset.toggleId || anchorElToggleFallbackId}"]`]
      : [],
  });

  const forkedRef = useForkRef(ref, clickOutsideTargetRef, menuContainerRef);

  // ========== anchorEl에 toggleId 설정 ==========
  useEffect(() => {
    if (anchorElHtmlElement && !anchorElHtmlElement.dataset.toggleId) {
      anchorElHtmlElement.dataset.toggleId = anchorElToggleFallbackId;
      return () => {
        delete anchorElHtmlElement.dataset.toggleId;
      };
    }
  }, [anchorElHtmlElement, anchorElToggleFallbackId]);

  // ========== Render ==========
  return (
    <DropdownMenu2Popper
      open={open}
      anchorEl={derivedAnchorEl}
      placement={placement}
      origin={origin}
      disablePortal={disablePortal}
      marginThreshold={marginThreshold}
      zIndex={zIndex}
      className={className}
      role={slotProps?.menuList?.role === 'listbox' ? 'presentation' : undefined}
      onTransitionEntered={handleTransitionEntered}
    >
      <StyledMenuContainer
        ref={forkedRef}
        apiRef={menuContainerApiRef}
        autoFocusItem={autoFocusItem}
        autoFocusSearchInput={autoFocusSearchInput}
        color={color}
        disableTooltipOnEllipsis={disableTooltipOnEllipsis}
        ellipsisTooltipPlacement={ellipsisTooltipPlacement}
        enableHighlight={enableHighlight}
        expandable={expandable}
        filterItems={filterItems}
        footer={footer}
        header={header}
        items={menuListItems}
        listRef={menuListRef}
        noFilteredOptionsText={noFilteredOptionsText}
        noOptionsText={noOptionsText}
        onKeyDown={handleContainerKeyDown}
        onSearchTextChange={onSearchTextChange}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        searchText={searchTextProp}
        selectedType={selectedType}
        sx={slotProps?.paper?.sx}
        {...slotProps?.menuContainer}
        slotProps={{ menuList: slotProps?.menuList }}
      />
      {/* </FcusTrap> */}
    </DropdownMenu2Popper>
  );
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledMenuContainer = styled(MenuContainer, {
  target: dropdownMenu2Classes.paper,
})<{ color: DropdownMenuColor }>`
  min-width: 160px;
  max-height: calc(100vh - 88px - 32px);
  border-radius: 0;
  color: ${({ theme, color }) =>
    color !== 'global' ? theme.color['text/default'] : theme.color['text/global/default']};
  background-color: ${({ theme, color }) =>
    color !== 'global' ? theme.color['base/white'] : theme.color['surface/global/strong/default']};
  box-shadow: ${({ theme }) => theme.shadows[2]};
`;
